// Route tree (code-based, not file-based generation) for the Goblic web
// app (FD §22.1 main navigation).
//
// TanStack Router's file-based generator (a `routeTree.gen.ts` produced by
// its Vite plugin) needs that plugin wired into vite.config.ts and a build
// step to run before `npm run dev` works; until that's set up this scaffold
// declares the tree by hand with createRootRoute/createRoute so the app
// doesn't depend on codegen having already run.

import { useQuery } from '@tanstack/react-query'
import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  isRedirect,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'
import type { WorkflowEditSearch } from './components/workflow-editor/workflow-editor'
import { authApi, meApi, streamsApi, tasksApi } from './lib/api-client'
import { RequireProject, useCurrentProject } from './lib/project-context'
import { LoadingRows } from './components/ui'
import { AppShell } from './components/app-shell'
import { safeNextPath } from './lib/safe-next'
import { currentUser, isAuthenticated } from './lib/auth'
import { queryClient } from './lib/query-client'
import { ApprovalsPage } from './routes/approvals'
import { AuditPage } from './routes/audit'
import { ChatPage } from './routes/chat/index'
import { ChatConversationPage } from './routes/chat/detail'
import {
  ConnectGitHubPage,
  type ConnectGitHubSearch,
} from './routes/connect-github'
import { CredentialsPage } from './routes/credentials'
import {
  WELCOME_STEPS,
  WelcomePage,
  type WelcomeSearch,
  type WelcomeStep,
} from './routes/welcome'
import { DashboardPage } from './routes/dashboard'
import { ExecutorsPage } from './routes/executors'
import { InvitePage } from './routes/invite'
import { LoginPage } from './routes/login'
import { OAuthConsentPage } from './routes/oauth-consent'
import { SetupPage } from './routes/setup'
import { McpServicesPage } from './routes/mcp-services'
import { KnowledgePage, validateKnowledgeSearch } from './routes/knowledge'
import { ProjectOverviewPage } from './routes/project/overview'
import { ProjectBoardPage, type BoardSearch } from './routes/project/board'
import { ProjectTasksPage } from './routes/project/tasks'
import {
  PROJECT_SETTINGS_TABS,
  ProjectSettingsPage,
  type ProjectSettingsSearch,
} from './routes/project/settings'
import {
  ProjectAgentsPage,
  ProjectKnowledgePage,
  ProjectReportsPage,
  ProjectRunsPage,
} from './routes/project/sections'
import { RolesPage } from './routes/roles'
import { RunDetailPage } from './routes/runs/detail'
import { RunsListPage } from './routes/runs/index'
import { SettingsPage } from './routes/settings'
import { SandboxHostsPage } from './routes/installation/sandbox-hosts'
import { SkillsPage } from './routes/skills'
import { TasksPage, type TasksSearch } from './routes/tasks'
import { TaskReportPage } from './routes/task-report'
import { WallPage } from './routes/wall'

export const rootRoute = createRootRoute({
  component: () => <Outlet />,
})

const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup',
  component: SetupPage,
  beforeLoad: async () => {
    try {
      const { needed } = await authApi.checkSetup()
      if (!needed) throw redirect({ to: '/login' })
    } catch (error) {
      if (isRedirect(error)) throw error
      // API unreachable — show the setup form as fallback
    }
  },
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { next?: string } => {
    const next = safeNextPath(search.next)
    return next ? { next } : {}
  },
  beforeLoad: async () => {
    try {
      const { needed } = await authApi.checkSetup()
      if (needed) throw redirect({ to: '/setup' })
    } catch (error) {
      if (isRedirect(error)) throw error
      // API unreachable — show the login form as fallback
    }
  },
})

/** Public: accept a workspace invitation (no auth guard, works signed in or out). */
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: InvitePage,
})

/**
 * MCP OAuth consent: outside the shell like login. Signed out, it goes via
 * /login and comes back here (`?next=`), keeping the request id.
 */
const oauthConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth/consent',
  component: OAuthConsentPage,
  validateSearch: (search: Record<string, unknown>): { request?: string } =>
    typeof search.request === 'string' && search.request
      ? { request: search.request }
      : {},
  beforeLoad: ({ location }) => {
    if (!isAuthenticated()) {
      throw redirect({
        to: '/login',
        search: { next: safeNextPath(location.pathname + location.searchStr) },
      })
    }
  },
})

/** The first-login welcome: its own full-page layout, but signed in like the rest. */
const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/welcome',
  component: WelcomePage,
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  validateSearch: (search: Record<string, unknown>): WelcomeSearch => ({
    ...(WELCOME_STEPS.includes(search.step as WelcomeStep)
      ? { step: search.step as WelcomeStep }
      : {}),
    ...(typeof search.project === 'string' && search.project
      ? { project: search.project }
      : {}),
  }),
})

/** The read-only wall dashboard: fullscreen, outside the app shell, but signed in like the rest. */
const wallRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/wall',
  component: WallPage,
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
})

/** Pathless layout route: everything except /login renders inside AppShell and requires a token. */
const shellRoute = createRoute({
  id: '_authenticated',
  getParentRoute: () => rootRoute,
  component: AppShell,
  beforeLoad: () => {
    // TODO: redirect back to the originally requested path after a
    // successful login (currently always lands on /dashboard).
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
})

/** `/` → the last project's overview, else All projects. */
function IndexRedirect() {
  const { project, isLoading } = useCurrentProject()
  if (isLoading) return <LoadingRows />
  return project ? (
    <Navigate to="/p/$projectKey/overview" params={{ projectKey: project.key }} replace />
  ) : (
    <Navigate to="/dashboard" replace />
  )
}

const indexRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  component: IndexRedirect,
})

const dashboardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/dashboard',
  component: DashboardPage,
})
/** Search of the tasks pages: `?new=true` opens "New task", `?taskId=` that task's drawer (workflow editor's usage overlay, ontwerp §8, links here), `?tab=report` opens it on its Report tab, `?stream=` filters. */
function validateTasksSearch(search: Record<string, unknown>): TasksSearch {
  return {
    ...(search.new === true || search.new === 'true' ? { new: true } : {}),
    ...(typeof search.taskId === 'string' ? { taskId: search.taskId } : {}),
    ...(search.tab === 'report' || search.tab === 'details' ? { tab: search.tab } : {}),
    ...(typeof search.stream === 'string' && search.stream ? { stream: search.stream } : {}),
  }
}

/**
 * `/tasks` (old links: Home, reports, the editor's usage overlay, Plane text)
 * → the same search on `/p/<key>/tasks`. The key is the project of `?taskId`,
 * else the current/last project. Without projects the old page stays.
 */
function TasksEntry() {
  const search = tasksRoute.useSearch()
  const { project, projects, isLoading } = useCurrentProject()
  const taskQuery = useQuery({
    queryKey: ['task', search.taskId ?? ''],
    queryFn: () => tasksApi.get(search.taskId!),
    enabled: search.taskId != null,
    retry: false,
  })
  if (isLoading || (search.taskId && taskQuery.isLoading)) return <LoadingRows />
  const target = projects.find((p) => p.id === taskQuery.data?.projectId) ?? project
  if (!target) return <TasksPage />
  return (
    <Navigate
      to="/p/$projectKey/tasks"
      params={{ projectKey: target.key }}
      search={search}
      replace
    />
  )
}

const tasksRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/tasks',
  component: TasksEntry,
  validateSearch: validateTasksSearch,
})
/** A task's report, full screen (docs/plans/2026-10-02-rapporten-ontwerp.md §7). */
const taskReportRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/tasks/$taskId/report',
  component: TaskReportPage,
})
const runsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/runs',
  component: RunsListPage,
})
const runDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/runs/$runId',
  component: RunDetailPage,
})
const chatRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/chat',
  component: ChatPage,
})
const chatConversationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/chat/$conversationId',
  component: ChatConversationPage,
})
const rolesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/roles',
  component: RolesPage,
})
const skillsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/skills',
  component: SkillsPage,
})
/** Knowledge base (docs/plans/2026-10-02-kennisbank-ontwerp.md §10). */
const knowledgeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/knowledge',
  component: KnowledgePage,
  validateSearch: validateKnowledgeSearch,
})
const knowledgeSpaceRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/knowledge/$space',
  component: KnowledgePage,
  validateSearch: validateKnowledgeSearch,
})
/** `$` = the page path (`areas/api.md`) or a folder (`areas/`). */
const knowledgePathRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/knowledge/$space/$',
  component: KnowledgePage,
  validateSearch: validateKnowledgeSearch,
})
/** `/projects` (old links) → the current project's settings, else All projects. */
function ProjectsRedirect() {
  const { project, isLoading } = useCurrentProject()
  if (isLoading) return <LoadingRows />
  return project ? (
    <Navigate to="/p/$projectKey/settings" params={{ projectKey: project.key }} search={{}} replace />
  ) : (
    <Navigate to="/dashboard" replace />
  )
}
const projectsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/projects',
  component: ProjectsRedirect,
})

/** `/p/$projectKey` → its overview. */
const projectRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/p/$projectKey',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/p/$projectKey/overview', params })
  },
})
const projectOverviewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/p/$projectKey/overview',
  component: ProjectOverviewPage,
})

/** `/p/$projectKey/boards` → the first stream's board. */
function BoardsRedirect() {
  return (
    <RequireProject>
      {(project) => <FirstStreamRedirect projectId={project.id} projectKey={project.key} />}
    </RequireProject>
  )
}
function FirstStreamRedirect({ projectId, projectKey }: { projectId: string; projectKey: string }) {
  const streamsQuery = useQuery({
    queryKey: ['project', projectId, 'streams'],
    queryFn: () => streamsApi.list(projectId),
  })
  if (streamsQuery.isLoading) return <LoadingRows />
  const first = streamsQuery.data?.items[0]
  if (!first) return <Navigate to="/p/$projectKey/overview" params={{ projectKey }} replace />
  return (
    <Navigate
      to="/p/$projectKey/boards/$streamKey"
      params={{ projectKey, streamKey: first.key }}
      replace
    />
  )
}
const projectBoardsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/p/$projectKey/boards',
  component: BoardsRedirect,
})
const projectBoardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/p/$projectKey/boards/$streamKey',
  component: ProjectBoardPage,
  validateSearch: (search: Record<string, unknown>): BoardSearch =>
    typeof search.taskId === 'string' ? { taskId: search.taskId } : {},
})
const projectTasksRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/p/$projectKey/tasks',
  component: ProjectTasksPage,
  validateSearch: validateTasksSearch,
})
const projectAgentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/p/$projectKey/agents',
  component: ProjectAgentsPage,
})
const projectRunsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/p/$projectKey/runs',
  component: ProjectRunsPage,
})
const projectReportsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/p/$projectKey/reports',
  component: ProjectReportsPage,
})
const projectKnowledgeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/p/$projectKey/knowledge',
  component: ProjectKnowledgePage,
})
const projectSettingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/p/$projectKey/settings',
  component: ProjectSettingsPage,
  validateSearch: (search: Record<string, unknown>): ProjectSettingsSearch => {
    const tab = PROJECT_SETTINGS_TABS.find((t) => t === search.tab)
    return tab ? { tab } : {}
  },
})
/**
 * Visual workflow editor (docs/plans/2026-09-29-visuele-workflow-editor-*):
 * a full-page canvas, lazy-loaded (React Flow + dagre are sizeable and only
 * this route needs them) via `router.tsx`'s own code-based tree rather than
 * file-based codegen (see the module header above).
 * `workflowKey` is either an existing workflow's key, or the literal `new`
 * (see `?template=`/`?templateId=`, workflows-tab.tsx's "New workflow").
 */
const workflowEditorRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/projects/$projectId/workflows/$workflowKey/edit',
  component: lazyRouteComponent(
    () => import('./components/workflow-editor/workflow-editor'),
    'WorkflowEditorPage'
  ),
  validateSearch: (search: Record<string, unknown>): WorkflowEditSearch => ({
    template: typeof search.template === 'string' ? search.template : undefined,
    templateId:
      typeof search.templateId === 'string' ? search.templateId : undefined,
  }),
})

const mcpServicesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/mcp-services',
  component: McpServicesPage,
})
const executorsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/executors',
  component: ExecutorsPage,
})
const credentialsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/credentials',
  component: CredentialsPage,
})
const connectGitHubRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/connect/github',
  component: ConnectGitHubPage,
  validateSearch: (search: Record<string, unknown>): ConnectGitHubSearch => {
    const pick = (key: string) =>
      typeof search[key] === 'string' || typeof search[key] === 'number'
        ? String(search[key])
        : undefined
    return {
      code: pick('code'),
      state: pick('state'),
      installation_id: pick('installation_id'),
      setup_action: pick('setup_action'),
    }
  },
})
const approvalsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/approvals',
  component: ApprovalsPage,
  // `?id=` selects one approval in the inbox.
  validateSearch: (search: Record<string, unknown>): { id?: string } =>
    typeof search.id === 'string' ? { id: search.id } : {},
})
const auditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/audit',
  component: AuditPage,
})
const settingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings',
  component: SettingsPage,
})

/**
 * Installation-wide sandbox hosts (sandbox-hosts ontwerp §11): installation
 * administrators only. `GET /me` decides (§15.1 item 10), the token's claim
 * only when /me can't be reached; the API checks again on every request.
 */
const sandboxHostsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/installation/sandbox-hosts',
  component: SandboxHostsPage,
  beforeLoad: async () => {
    const me = await queryClient
      .ensureQueryData({ queryKey: ['me'], queryFn: meApi.get })
      .catch(() => null)
    const isAdmin = me ? me.isInstanceAdmin : currentUser()?.isInstanceAdmin
    if (!isAdmin) throw redirect({ to: '/' })
  },
})

export const routeTree = rootRoute.addChildren([
  setupRoute,
  loginRoute,
  inviteRoute,
  welcomeRoute,
  oauthConsentRoute,
  wallRoute,
  shellRoute.addChildren([
    indexRoute,
    dashboardRoute,
    tasksRoute,
    taskReportRoute,
    runsRoute,
    runDetailRoute,
    chatRoute,
    chatConversationRoute,
    rolesRoute,
    skillsRoute,
    knowledgeRoute,
    knowledgeSpaceRoute,
    knowledgePathRoute,
    projectsRoute,
    projectRoute,
    projectOverviewRoute,
    projectBoardsRoute,
    projectBoardRoute,
    projectTasksRoute,
    projectAgentsRoute,
    projectRunsRoute,
    projectReportsRoute,
    projectKnowledgeRoute,
    projectSettingsRoute,
    workflowEditorRoute,
    mcpServicesRoute,
    executorsRoute,
    credentialsRoute,
    connectGitHubRoute,
    approvalsRoute,
    auditRoute,
    settingsRoute,
    sandboxHostsRoute,
  ]),
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
